import { KeyboardEvent, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout, LayoutStorage, PanelImperativeHandle, useDefaultLayout, usePanelRef } from 'react-resizable-panels';
import { ArrowLeft, ExternalLink, Eye, Files, FlaskConical, History, Save, Send, Settings, Sparkles } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { relativeTime, shortName } from '@/frontend/lib/people';
import { cn } from '@/frontend/lib/utils';
import { ReportOutlineProvider, useNewOutlineStore } from '@/frontend/reports/primitives';
import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { ReportBundleEntry, useReportBundleStore } from '@/frontend/store/ReportBundleStore';
import { FusaToastType } from '@/frontend/toasts/types';
import { sampleRunReportContext } from '@/reports/bundles/fixture';
import { describeFixtureMiss } from '@/reports/bundles/fixtureMiss';
import { Check, DEFAULT_RULES_FILE, PrintedTarget, isRulesPath, readFixtureChecks, readRulesFile } from '@/reports/bundles/expectations';
import { checkFixturesPrinted, withPrinted } from '@/frontend/reports/printedChecks';
import { INPUT_SHEET_FILE, INPUT_SHEET_TYPES_FILE, SCHEMA_REF_FILE, SheetIdChange, sameSheet, sheetIdChanges } from '@/reports/bundles/inputSheet';
import { FixtureResult, REPORT_BUNDLE_DEFINITION_ENTRY, REPORT_BUNDLE_DOCUMENT_ENTRY, REPORT_BUNDLE_FIXTURE_DIR, ReportBundleFiles, isValidBundlePath } from '@/reports/bundles/types';
import { InputValues, inputsContext } from '@/reports/inputs';
import { InputsReportContext, ReportVerdict, RunReportContext } from '@/reports/types';
import { BundleSchemaInfo } from '@/types/reportBundleTypes';
import { LoadingSpinner } from '../LoadingSpinner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Button, buttonVariants } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../ui/resizable';
import { BundleCodeEditor, checkBundleTypes } from './BundleCodeEditor';
import { BundleContextPanel } from './BundleContextPanel';
import { BundlePreview } from './BundlePreview';
import { BundleVersionsPanel } from './BundleVersionsPanel';
import { BundleAssistantPanel } from './assistant/BundleAssistantPanel';
import { AssistantWorkspace } from './assistant/assistantTools';
import { useBundleAssistant } from './assistant/useBundleAssistant';
import { ChecksPanel, CheckTab } from './designer/ChecksPanel';
import { CompareSheetsDialog } from './designer/CompareSheets';
import { Explorer, NewFileKind } from './designer/Explorer';
import { DefinitionView, DocumentOutlineView, SchemaRefView } from './designer/FileViews';
import { FixtureView, newFixture, parseFixture, writeFixture } from './designer/FixtureView';
import { PickBanner, PickCard, PickScope } from './designer/PickCard';
import { RulesView, writeRules } from './designer/RulesView';
import { usePrintedChecks } from './designer/usePrintedChecks';
import { PreviewChoice, PreviewSidebar } from './designer/PreviewSidebar';
import { SettingsDialog } from './designer/SettingsDialog';
import { TestsView } from './designer/TestsView';
import { TryItBar, useDraftVerdict } from './designer/TryItBar';
import { Activity, ActivityBar, FileHeader, FileTabs, FileView, KindChip, SidebarHeader, StatusBar, StatusItem, baseName, fileKind } from './designer/designerChrome';
import { IdChangeList } from './inputs/IdChanges';
import { InputsTab, readSheetFile, writeSheetFile } from './inputs/InputsTab';
import { IconButton, IconLink, UnpublishedChip } from './studioChrome';

const ENTRIES = [REPORT_BUNDLE_DEFINITION_ENTRY, REPORT_BUNDLE_DOCUMENT_ENTRY];
/** what the bundle cannot be without: its entries, and the schema it reads and the types written from it */
const UNDELETABLE = [...ENTRIES, INPUT_SHEET_FILE, SCHEMA_REF_FILE, INPUT_SHEET_TYPES_FILE];
const previewRunKey = (bundleId: string) => `veriform.reportStudio.previewRun.${bundleId}`;

/** What this browser remembers of the studio — how its panels are laid out, the run in each bundle's preview — read without trusting storage to be there. */
function remembered(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // only the convenience is lost
  }
}

const layoutStorage: LayoutStorage = { getItem: remembered, setItem: remember };

/**
 * The designer's columns, as percentages: the side bar on the left (the
 * files, the assistant, the tests or the history, picked from the bar beside
 * it), the open file, and the preview on the right. The side bar and the
 * preview each fold away and come back at the width they had.
 */
type SideView = 'files' | 'assistant' | 'tests' | 'history';
const SIDE_VIEWS: SideView[] = ['files', 'assistant', 'tests', 'history'];
const SIDE_VIEW_KEY = 'veriform.reportStudio.sideView';
const DESIGNER_PANELS = ['side', 'main', 'preview'];
const DESIGNER_LAYOUT: Layout = { side: 17, main: 53, preview: 30 };

/** What the preview reads: a fixture, what is typed in Try it, the schema with nothing entered — or, for a run report, the run in hand. */
type PreviewSource = { kind: 'fixture'; path: string } | { kind: 'try' } | { kind: 'empty' } | { kind: 'run' };

/** The views a file can be shown in, the one it opens in first. */
function viewsOf(path: string, pinned: boolean): FileView[] {
  switch (fileKind(path)) {
    case 'schema': return pinned ? ['try'] : ['design', 'try', 'code'];
    case 'fixture':
    case 'schemaRef': return ['visual', 'code'];
    case 'generated': return ['code'];
    case 'test': return isRulesPath(path) ? ['visual', 'code'] : ['code'];
    default: return ENTRIES.includes(path) ? ['visual', 'code'] : ['code'];
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/** Fold a side panel away or bring it back; a group not laid out yet (no size) keeps the layout it has. */
function fold(panel: { current: PanelImperativeHandle | null }, open: boolean) {
  try {
    if (open) panel.current?.expand();
    else panel.current?.collapse();
  } catch {
    // nothing to resize until the group has a size
  }
}

/** The first `fixtures/<base>.json` free among these files. */
function freeFixturePath(files: ReportBundleFiles, base: string): string {
  let n = 1;
  const at = (i: number) => `${REPORT_BUNDLE_FIXTURE_DIR}${base}${i === 1 ? '' : `-${i}`}.json`;
  while (at(n) in files) n += 1;
  return at(n);
}

/**
 * A published version's source as the draft, with the schema the draft reads
 * kept as it is: a version's `schema.json` pins the schema version it was
 * published against, and restoring its code is no reason to stop following
 * the schema's draft — nor, for one published before schemas, to save its old
 * sheet over the schema's.
 */
function restoreKeepingSchema(restored: ReportBundleFiles, working: ReportBundleFiles | null): ReportBundleFiles {
  if (!working || !(SCHEMA_REF_FILE in working)) return restored;
  const { [INPUT_SHEET_FILE]: _sheet, [INPUT_SHEET_TYPES_FILE]: _types, ...code } = restored;
  const kept = Object.fromEntries([SCHEMA_REF_FILE, INPUT_SHEET_FILE, INPUT_SHEET_TYPES_FILE].filter((p) => p in working).map((p) => [p, working[p]]));
  return { ...code, ...kept };
}

/**
 * One report, in a designer laid out like a code editor: its files on the
 * left, each opened in a tab and shown visually or as its code — the two are
 * the same file — and the saved draft running in the preview on the right,
 * over a fixture, what is typed in Try it, or a run. Save compiles and runs
 * every fixture; publish freezes the draft as the version report templates use
 * from then on. The assistant works on the same files, saves through the same
 * Save, and reads the same preview.
 */
export function ReportStudioEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const addToast = useGlobalStore((s) => s.addToast);
  const entry = useReportBundleStore((s) => s.bundlesById[id]);
  const sdkTypes = useReportBundleStore((s) => s.sdkTypes);
  const fetchBundle = useReportBundleStore((s) => s.fetchBundle);
  const fetchSdkTypes = useReportBundleStore((s) => s.fetchSdkTypes);
  const fetchVersion = useReportBundleStore((s) => s.fetchVersion);
  const saveDraft = useReportBundleStore((s) => s.saveDraft);
  const publish = useReportBundleStore((s) => s.publish);
  const updateMeta = useReportBundleStore((s) => s.updateMeta);
  const deleteBundle = useReportBundleStore((s) => s.deleteBundle);
  const readPreviewContext = useReportBundleStore((s) => s.readPreviewContext);
  const assistantStatus = useReportBundleStore((s) => s.assistantStatus);
  const fetchAssistantStatus = useReportBundleStore((s) => s.fetchAssistantStatus);

  const [files, setFiles] = useState<ReportBundleFiles | null>(null);
  /** the open files, in their tabs' order; null until the files are here to pick the first ones */
  const [tabs, setTabs] = useState<string[] | null>(null);
  const [active, setActive] = useState<string>(REPORT_BUNDLE_DOCUMENT_ENTRY);
  /** the view each file was last shown in; its first view when never switched */
  const [views, setViews] = useState<Record<string, FileView>>({});
  const [reveal, setReveal] = useState<{ path: string; line: number } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState<RunReportContext>(() => sampleRunReportContext());
  /** the real run in the preview, when one is loaded; null: the sample, or a context typed by hand */
  const [previewRun, setPreviewRun] = useState<{ id: string; title: string } | null>(null);
  /** what the author picked for the preview to read; null: the first fixture, or for a run report the run */
  const [previewSource, setPreviewSource] = useState<PreviewSource | null>(null);
  /** what is typed in Try it: kept here so the preview reads it and it can be saved as a fixture */
  const [tried, setTried] = useState<InputValues>({});
  const [checks, setChecks] = useState<CheckTab | null>(null);
  const [showPublish, setShowPublish] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRun, setShowRun] = useState(false);
  const [adding, setAdding] = useState<NewFileKind | null>(null);
  const [notFound, setNotFound] = useState(false);
  /** a save refused the working inputs.json: the schema's draft changed since it was opened. It stays as typed until the author picks one */
  const [sheetConflict, setSheetConflict] = useState(false);
  const [comparing, setComparing] = useState(false);
  /** picking a place in the preview to check, and what was picked */
  const [picking, setPicking] = useState(false);
  /** `in`: the preview it was made in; it is shown only while that one is (see `shownPick`) */
  const [picked, setPicked] = useState<{ target: PrintedTarget; found: string[]; in: string } | null>(null);
  /** the latest published version's files, to mark what changed since; null before they are read, or with none published */
  const [published, setPublished] = useState<ReportBundleFiles | null>(null);
  /** what the preview prints, section by section, for document.tsx's Visual view */
  const outline = useNewOutlineStore();

  // What the assistant's tools read runs long after the render that made them, so they read through refs.
  const filesRef = useRef(files);
  filesRef.current = files;
  const entryRef = useRef<ReportBundleEntry | undefined>(entry);
  entryRef.current = entry;
  const contextRef = useRef(context);
  contextRef.current = context;
  const previewRunRef = useRef(previewRun);
  previewRunRef.current = previewRun;
  const activeRef = useRef(active);
  activeRef.current = active;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  // the schema draft the working files' inputs.json was opened from, sent with every save so a stale sheet never overwrites a newer draft
  const schemaBase = useRef<string | undefined>(undefined);
  /** the draft hash the loaded run was last read for */
  const contextHashRef = useRef<string | null>(null);

  useEffect(() => {
    void fetchBundle(id).then((loaded) => setNotFound(loaded === null));
    void fetchSdkTypes();
  }, [id, fetchBundle, fetchSdkTypes]);

  useEffect(() => {
    void fetchAssistantStatus();
  }, [fetchAssistantStatus]);

  const [sideView, setSideView] = useState<SideView>(() => {
    const view = remembered(SIDE_VIEW_KEY) as SideView | null;
    return view && SIDE_VIEWS.includes(view) ? view : 'files';
  });
  const designerLayout = useDefaultLayout({ id: 'veriform.reportStudio.designerLayout', panelIds: DESIGNER_PANELS, storage: layoutStorage });
  const [startLayout] = useState(() => designerLayout.defaultLayout ?? DESIGNER_LAYOUT);
  const [sideOpen, setSideOpen] = useState(() => (startLayout.side ?? 0) > 0);
  const [previewOpen, setPreviewOpen] = useState(() => (startLayout.preview ?? 0) > 0);
  const sidePanel = usePanelRef();
  const previewPanel = usePanelRef();

  /** Show one view in the side bar; picking the one showing folds the side bar away. */
  const pickSide = (view: SideView) => {
    if (sideOpen && view === sideView) {
      setSideOpen(false);
      fold(sidePanel, false);
      return;
    }
    remember(SIDE_VIEW_KEY, view);
    setSideView(view);
    if (!sideOpen) {
      setSideOpen(true);
      fold(sidePanel, true);
    }
  };
  /** Make sure a view is showing, without folding it if it already is. */
  const showSide = (view: SideView) => {
    if (!sideOpen || view !== sideView) pickSide(view);
  };
  const togglePreview = () => {
    setPreviewOpen(!previewOpen);
    fold(previewPanel, !previewOpen);
  };

  /** Open a file in a tab, or go to its tab, in the view given or the one it was last in. */
  const openFile = (path: string, view?: FileView) => {
    setTabs((current) => (current === null || current.includes(path) ? current : [...current, path]));
    setActive(path);
    if (view) setViews((all) => ({ ...all, [path]: view }));
    // a fixture opened is a fixture to look at: the preview reads it
    if (fileKind(path) === 'fixture') setPreviewSource({ kind: 'fixture', path });
  };
  const closeFile = (path: string) => {
    const current = tabs ?? [];
    const at = current.indexOf(path);
    const next = current.filter((p) => p !== path);
    setTabs(next);
    if (active === path) setActive(next[Math.min(at, next.length - 1)] ?? '');
  };

  /** Put a real run in the preview, read as the saved draft reads it. Returns why it could not, or null. */
  const loadRun = async (runId: string): Promise<string | null> => {
    const read = await readPreviewContext(id, runId);
    if ('error' in read) return read.error;
    contextRef.current = read.context;
    contextHashRef.current = entryRef.current?.bundle.draftCompiled?.hash ?? null;
    setContext(read.context);
    setPreviewRun({ id: runId, title: read.context.run.title });
    remember(previewRunKey(id), runId);
    return null;
  };

  // the run this bundle was last previewed with, once its draft is here to read it
  const bundleLoaded = !!entry;
  useEffect(() => {
    const runId = remembered(previewRunKey(id));
    if (bundleLoaded && runId && !previewRunRef.current) void loadRun(runId).then((problem) => { if (problem) remember(previewRunKey(id), null); });
  }, [id, bundleLoaded]);

  // A new draft may ask the run for other point files: read the loaded run again for it.
  const draftHash = entry?.bundle.draftCompiled?.hash ?? null;
  useEffect(() => {
    const run = previewRunRef.current;
    if (run && draftHash && draftHash !== contextHashRef.current) void loadRun(run.id);
  }, [draftHash]);

  // The latest version's files, to mark what the draft changed since.
  const latestVersionId = entry?.versions[0]?.id ?? null;
  useEffect(() => {
    if (!latestVersionId) {
      setPublished(null);
      return;
    }
    let live = true;
    void fetchVersion(id, latestVersionId).then((version) => { if (live) setPublished(version?.files ?? null); }, () => undefined);
    return () => { live = false; };
  }, [id, latestVersionId, fetchVersion]);

  // A report filled in from inputs opens on its schema, a run report on its document.
  useEffect(() => {
    if (!files || tabs !== null) return;
    const readsInputs = INPUT_SHEET_FILE in files || !!entryRef.current?.schema;
    setTabs(readsInputs ? [REPORT_BUNDLE_DOCUMENT_ENTRY, INPUT_SHEET_FILE] : [REPORT_BUNDLE_DEFINITION_ENTRY, REPORT_BUNDLE_DOCUMENT_ENTRY]);
    setActive(readsInputs ? INPUT_SHEET_FILE : REPORT_BUNDLE_DOCUMENT_ENTRY);
  }, [files, tabs]);

  // The stored draft is the baseline until the author types; a save re-baselines. The schema's draft is
  // among the files, and may have been saved on its own page or through another bundle since.
  useEffect(() => {
    if (!entry || dirty) return;
    setFiles(entry.bundle.draftFiles);
    schemaBase.current = entry.schema?.draftHash;
  }, [entry?.bundle.draftUpdatedAt, entry?.bundle.id, entry?.schema?.draftHash]);

  // The preview: the draft as last compiled, over what the author picked for it to read.
  const draftSheet = entry?.bundle.draftCompiled?.subject === 'inputs' ? entry.bundle.draftCompiled.sheet ?? null : null;
  // a report following its schema's draft is compiled against the sheet of its own last save: a schema saved since leaves
  // that compile, and every check made on it, behind; it is saved again (compiled and checked) before it is published
  const storedSheet = entry?.schema && entry.schema.pinnedVersion === null && draftSheet ? readSheetFile(entry.bundle.draftFiles[INPUT_SHEET_FILE]).sheet : null;
  const sheetMoved = !!entry?.schema && entry.schema.pinnedVersion === null && !!draftSheet && !(storedSheet && sameSheet(draftSheet, storedSheet));
  const readsInputs = !!draftSheet || INPUT_SHEET_FILE in (files ?? {}) || !!entry?.schema;
  const fixturePaths = useMemo(() => Object.keys(files ?? {}).filter((p) => fileKind(p) === 'fixture').sort(), [files]);
  const source: PreviewSource = previewSource && (previewSource.kind !== 'fixture' || fixturePaths.includes(previewSource.path))
    ? previewSource
    : readsInputs ? (fixturePaths[0] ? { kind: 'fixture', path: fixturePaths[0] } : { kind: 'empty' }) : { kind: 'run' };
  const sourceText = source.kind === 'fixture' ? files?.[source.path] : undefined;
  // a pick is of the document it was made in: another one previewed, it is let go rather than written into that one
  const sourceKey = source.kind === 'fixture' ? `fixture:${source.path}` : source.kind === 'run' ? `run:${previewRun?.id ?? 'sample'}` : source.kind;
  const shownPick = picked && picked.in === sourceKey ? picked : null;
  const preview = useMemo<{ context: RunReportContext | InputsReportContext; values?: Record<string, unknown>; from: string }>(() => {
    const fixture = source.kind === 'fixture' ? parseFixture(sourceText) : null;
    const from = source.kind === 'fixture' ? source.path : source.kind === 'try' ? 'what the author typed in Try it (not saved)' : 'no fixture (every input empty)';
    if (draftSheet) return { context: inputsContext(draftSheet, source.kind === 'try' ? tried : fixture?.inputs ?? {}, fixture?.traces), values: fixture?.values, from };
    if (fixture && isObject(fixture.context) && isObject(fixture.context.run)) return { context: fixture.context as unknown as RunReportContext, values: fixture.values, from: `${from} (its context.run)` };
    return { context, from: previewRun ? `run "${previewRun.title}" (id ${previewRun.id})` : 'the sample run; no real run is loaded' };
  }, [draftSheet, source.kind, sourceText, tried, context, previewRun]);
  const sheetPreviewRef = useRef<{ context: InputsReportContext; from: string } | null>(null);
  sheetPreviewRef.current = draftSheet ? { context: preview.context as InputsReportContext, from: preview.from } : null;
  const previewFromRef = useRef(preview.from);
  previewFromRef.current = preview.from;
  const triedContext = useMemo(() => (draftSheet ? inputsContext(draftSheet, tried) : null), [draftSheet, tried]);
  const triedVerdict = useDraftVerdict(entry?.bundle.draftCompiled ?? null, triedContext);

  /**
   * After a save the schema did not refuse, with the working files kept: the base moves to the stored draft only
   * with that draft as the working sheet, or a draft saved elsewhere meanwhile would be saved over unseen.
   */
  const rebaseSheet = (sent: ReportBundleFiles, saved: { bundle: { draftFiles: ReportBundleFiles }; schema?: { draftHash: string } | null }) => {
    const stored = saved.bundle.draftFiles[INPUT_SHEET_FILE];
    const working = filesRef.current;
    if (stored === undefined || stored === sent[INPUT_SHEET_FILE]) {
      schemaBase.current = saved.schema?.draftHash;
    } else if (working && working[INPUT_SHEET_FILE] === sent[INPUT_SHEET_FILE]) {
      // the sheet was left as it was: it becomes the draft as stored now
      const next = { ...working, [INPUT_SHEET_FILE]: stored };
      filesRef.current = next;
      setFiles(next);
      schemaBase.current = saved.schema?.draftHash;
    }
    // else the author changed the sheet since: the base stays, so saving it conflicts
  };

  const workspace = useMemo<AssistantWorkspace>(() => ({
    getFiles: () => filesRef.current ?? {},
    setFiles: (next, changed) => {
      filesRef.current = next;
      setFiles(next);
      setDirty(true);
      if (changed && changed in next) {
        setTabs((current) => (current === null || current.includes(changed) ? current : [...current, changed]));
        setActive(changed);
      }
    },
    compile: async () => {
      const sent = filesRef.current;
      if (!sent) return null;
      const saved = await saveDraft(id, sent, { quiet: true, schemaDraftHash: schemaBase.current });
      if (!saved) return null;
      // the author may have typed while it compiled; only what was sent is saved, and a sheet the schema refused stays unsaved
      const typed = filesRef.current !== sent;
      if (saved.schemaConflict) setSheetConflict(true);
      else rebaseSheet(sent, saved);
      if (!typed && !saved.schemaConflict) setDirty(false);
      if (previewRunRef.current && saved.bundle.draftCompiled && saved.bundle.draftCompiled.hash !== contextHashRef.current) await loadRun(previewRunRef.current.id);
      // the fixtures as the studio holds them: the printed document's checks, made here, beside the compile's own
      const compiled = saved.bundle.draftCompiled;
      if (!compiled || !(compiled.fixtureResults ?? []).some((r) => (r.printed ?? 0) > 0)) return { compiled, diagnostics: saved.diagnostics };
      const printed = await checkFixturesPrinted(compiled, saved.bundle.draftFiles);
      return { compiled: { ...compiled, fixtureResults: withPrinted(compiled.fixtureResults ?? [], printed) }, diagnostics: saved.diagnostics };
    },
    getCompiled: () => ({ compiled: entryRef.current?.bundle.draftCompiled ?? null, files: entryRef.current?.bundle.draftFiles ?? {} }),
    getContext: () => contextRef.current,
    getInputsPreview: () => sheetPreviewRef.current,
    isSampleContext: () => previewRunRef.current === null,
    loadRun,
    getSdkFiles: () => useReportBundleStore.getState().sdkTypes?.files ?? {},
    typeCheck: () => checkBundleTypes(filesRef.current ?? {}),
  }), [id]);

  const previewLabel = source.kind === 'fixture' ? baseName(source.path)
    : source.kind === 'try' ? I18n.get('designerPreviewTry')
      : source.kind === 'empty' ? I18n.get('designerPreviewEmpty')
        : previewRun ? previewRun.title : I18n.get('designerPreviewSample');
  /** what the assistant's panel says the preview reads */
  const assistantPreviewLabel = source.kind !== 'run' ? previewLabel
    : previewRun ? I18n.get('reportAssistantPreviewRun', { title: previewRun.title, id: previewRun.id }) : I18n.get('reportAssistantPreviewSample');
  const assistant = useBundleAssistant({
    bundleId: id,
    workspace,
    studioState: () => {
      const current = entryRef.current;
      const saved = current?.bundle.draftFiles ?? {};
      const working = filesRef.current ?? {};
      return {
        bundleKey: current?.bundle.key ?? '',
        bundleTitle: current?.bundle.title ?? '',
        activeFile: activeRef.current,
        previewRun: sheetPreviewRef.current ? `the input sheet (inputs.json) as ${sheetPreviewRef.current.from} fills it in; this report reads no run` : previewFromRef.current,
        compiles: !!current?.bundle.draftCompiled,
        problems: current?.diagnostics.length ?? 0,
        dirtyFiles: dirtyRef.current ? Object.keys({ ...saved, ...working }).filter((p) => saved[p] !== working[p]).sort() : [],
      };
    },
  });

  const handleChange = (path: string, content: string) => {
    setFiles((current) => (current ? { ...current, [path]: content } : current));
    setDirty(true);
  };

  /** Save the working files; null when they were not all saved. */
  /** `over`: the `filesHash` of the draft the files were taken from, when nothing was typed: a save made for the author's sake, never over another's */
  const save = async (over?: string) => {
    const sent = filesRef.current;
    if (!sent) return null;
    setSaving(true);
    try {
      const saved = await saveDraft(id, sent, { schemaDraftHash: schemaBase.current, ...(over ? { expectedDraftHash: over } : {}) });
      if (!saved) return null;
      // the author may have typed while it saved: what they typed stays, unsaved
      const typed = filesRef.current !== sent;
      if (saved.schemaConflict) {
        // the schema's draft changed since this sheet was opened: it stays as the author typed it, unsaved, and the banner says why
        if (!typed) setFiles({ ...saved.bundle.draftFiles, [INPUT_SHEET_FILE]: sent[INPUT_SHEET_FILE] });
        setSheetConflict(true);
        return null;
      }
      setSheetConflict(false);
      if (saved.bundle.draftCompiled === null) setChecks('problems');
      if (typed) {
        rebaseSheet(sent, saved);
        return null;
      }
      schemaBase.current = saved.schema?.draftHash;
      setDirty(false);
      setFiles(saved.bundle.draftFiles);
      return saved;
    } finally {
      setSaving(false);
    }
  };
  const handleSave = async (): Promise<boolean> => {
    const saved = await save();
    return !!saved && saved.bundle.draftCompiled !== null;
  };

  /** The schema's draft changed under the working sheet: take the draft as it is now, or save the working sheet over it. */
  const resolveSheetConflict = (keep: 'theirs' | 'mine') => {
    const current = entryRef.current;
    if (!current || !files) return;
    setSheetConflict(false);
    schemaBase.current = current.schema?.draftHash;
    if (keep === 'mine') {
      void handleSave();
      return;
    }
    const stored = current.bundle.draftFiles;
    const next: ReportBundleFiles = { ...files, [INPUT_SHEET_FILE]: stored[INPUT_SHEET_FILE] };
    setFiles(next);
    setDirty(Object.keys({ ...stored, ...next }).some((p) => stored[p] !== next[p]));
  };

  /** A link followed in this tab with unsaved changes (the schema's own page, the list): saved first, or what is typed here is lost on the way. */
  const leaveTo = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    void save().then((saved) => { if (saved) void navigate(to); });
  };

  /** Publish, saving first what is unsaved; false when the dialog is to stay open to show why it was not. */
  const handlePublish = async (note: string, publishSchema: boolean): Promise<boolean> => {
    // the schema's draft is published only as the sheet the dialog compared: refused if it moved on since
    const compared = files?.[INPUT_SHEET_FILE];
    let schemaDraftHash = schemaBase.current;
    // the draft whose checks were made here, and none saved since: the server makes no check on the printed document
    let expectedDraftHash = entry?.filesHash ?? undefined;
    if (dirty || sheetMoved) {
      // nothing typed, only the schema moved: saved over the draft this page shows, and refused when another was saved since
      const saved = await save(dirty ? undefined : expectedDraftHash);
      if (!saved || saved.bundle.draftCompiled === null) return true;
      // the save brought back a draft saved elsewhere instead: the hash the dialog was shown gets the publish refused
      if (saved.bundle.draftFiles[INPUT_SHEET_FILE] === compared) schemaDraftHash = schemaBase.current;
      // the server makes no check on the printed document: the draft just saved is held to them here, as one saved before is by the dialog
      const compiled = saved.bundle.draftCompiled;
      // a fixture that misses already: the server would refuse it, and the dialog says why
      if ((compiled.fixtureResults ?? []).some((r) => !r.ok)) return false;
      if ((compiled.fixtureResults ?? []).some((r) => (r.printed ?? 0) > 0)) {
        const printed = await checkFixturesPrinted(compiled, saved.bundle.draftFiles);
        if (withPrinted(compiled.fixtureResults ?? [], printed).some((r) => !r.ok)) return false;
      }
      expectedDraftHash = saved.filesHash ?? undefined;
    }
    const version = await publish(id, note, { publishSchema, schemaDraftHash, expectedDraftHash });
    if (version) showSide('history');
    return true;
  };

  /** Add a file to the working files and open it. */
  const addFile = (path: string, content: string, view?: FileView) => {
    const current = filesRef.current;
    if (!current || !isValidBundlePath(path) || path in current) return;
    const next = { ...current, [path]: content };
    filesRef.current = next;
    setFiles(next);
    setDirty(true);
    openFile(path, view);
  };

  const handleNewFile = (kind: NewFileKind) => {
    const current = filesRef.current;
    if (!current) return;
    if (kind === 'rules') {
      let n = 1;
      const at = (i: number) => (i === 1 ? DEFAULT_RULES_FILE : `tests/rules-${i}.json`);
      while (at(n) in current) n += 1;
      addFile(at(n), writeRules({ checks: [] }), 'visual');
      return;
    }
    if (kind !== 'fixture') {
      setAdding(kind);
      return;
    }
    const sheet = entry?.schema?.pinnedSheet ?? readSheetFile(current[INPUT_SHEET_FILE]).sheet;
    const path = freeFixturePath(current, 'new-fixture');
    addFile(path, writeFixture(newFixture(readsInputs ? sheet : null, 'passed', undefined, context)), 'visual');
  };

  /** What is typed in Try it, kept as a fixture that must come to the verdict picked: saved, and opened. */
  const saveTriedAsFixture = (path: string, verdict: ReportVerdict) => {
    const current = filesRef.current;
    if (!current) return;
    const sheet = entry?.schema?.pinnedSheet ?? readSheetFile(current[INPUT_SHEET_FILE]).sheet;
    addFile(path, writeFixture(newFixture(sheet, verdict, tried)), 'visual');
    setPreviewSource({ kind: 'fixture', path });
    void handleSave();
  };

  /** Pick what a check is about in the preview: for the fixture open, in its own document. */
  const startPick = () => {
    if (active && fileKind(active) === 'fixture') setPreviewSource({ kind: 'fixture', path: active });
    setPicked(null);
    setPicking(true);
    if (!previewOpen) togglePreview();
  };
  const stopPick = () => {
    setPicking(false);
    setPicked(null);
  };

  /** A check written from what was picked: into the fixture the preview reads, or a rules file for every fixture. */
  const addPicked = (check: Check, scope: PickScope, fixture: string | null, rulesFile: string) => {
    const current = filesRef.current;
    if (!current) return;
    let next: ReportBundleFiles;
    if (scope === 'fixture' && fixture) {
      const parsed = parseFixture(current[fixture]);
      // as with a rules file: a fixture with a check it cannot read is fixed in code first
      if (!parsed || !fixtureTakesChecks(current[fixture])) return;
      next = { ...current, [fixture]: writeFixture({ ...parsed, expect: { ...(parsed.expect ?? {}), checks: [...((parsed.expect?.checks as Check[] | undefined) ?? []), check] } }) };
    } else {
      // a rules file with something unread in it is fixed in code first, as its own view asks
      if (rulesFile in current && readRulesFile(current[rulesFile]).problems.length > 0) return;
      // added to the file as it is: a check in it that could not be read stays for the author to fix in code
      const raw = rulesFile in current ? JSON.parse(current[rulesFile]) as Record<string, unknown> : {};
      next = { ...current, [rulesFile]: writeRules({ ...raw, checks: [...(Array.isArray(raw.checks) ? raw.checks : []), check] } as Parameters<typeof writeRules>[0]) };
    }
    filesRef.current = next;
    setFiles(next);
    setDirty(true);
    setPicked(null);
    const into = scope === 'fixture' && fixture ? fixture : rulesFile;
    setTabs((open) => (open === null || open.includes(into) ? open : [...open, into]));
    setActive(into);
  };

  const handleDeleteFile = (path: string) => {
    if (!files || UNDELETABLE.includes(path)) return;
    const { [path]: _gone, ...rest } = files;
    setFiles(rest);
    if (tabs?.includes(path)) closeFile(path);
    setDirty(true);
  };

  // the printed document's checks, made here on the draft as last saved
  const printedChecks = usePrintedChecks(entry?.bundle.draftCompiled ?? null, entry?.bundle.draftFiles);

  if (notFound) {
    return (
      <div data-slot="report-studio" className="flex flex-col items-start gap-2 p-[1.5rem] text-sm">
        <p className="text-muted-foreground">{I18n.get('modelNotFound', { model: I18n.get('reportBundle') })}</p>
        <Link to="/report-studio" className={buttonVariants({ variant: 'outline', size: 'sm' })}><ArrowLeft /> {I18n.get('reportBundleBackToList')}</Link>
      </div>
    );
  }
  if (!entry || !files) {
    return <div className="d-flex justify-content-center py-5" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;
  }

  const { bundle, versions, diagnostics, schema } = entry;
  const compiled = bundle.draftCompiled;
  const compiles = compiled !== null;
  const results = withPrinted(compiled?.fixtureResults ?? [], printedChecks.byFile, printedChecks.pending);
  const failing = results.filter((r) => !r.ok).length;
  const latest = versions[0] ?? null;
  const unpublished = !!latest && (dirty || latest.hash !== compiled?.hash);
  // a pinned schema version is shown, not edited: it is never among the working files
  const pinnedSheet = schema?.pinnedSheet ?? null;
  const which = schema ? (schema.pinnedVersion === null ? I18n.get('designerDraft') : `v${schema.pinnedVersion}`) : '';
  const schemaLine = schema ? I18n.get('designerSchemaLine', { title: schema.title, which }) : null;
  const rank = (path: string) => (ENTRIES.includes(path) ? ENTRIES.indexOf(path) : ENTRIES.length);
  const paths = [...Object.keys(files), ...(pinnedSheet && !(INPUT_SHEET_FILE in files) ? [INPUT_SHEET_FILE] : [])]
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  const openTabs = (tabs ?? []).filter((p) => paths.includes(p));
  const current = openTabs.includes(active) ? active : openTabs[0] ?? null;
  const fileViews = current ? viewsOf(current, !!pinnedSheet) : [];
  const view: FileView = current && views[current] && fileViews.includes(views[current]) ? views[current] : fileViews[0] ?? 'code';
  const showCode = current !== null && view === 'code';
  // Monaco stays mounted under every other view, on the last file it can show: it keeps its models, undo history and type checks
  const codePath = current && current in files ? current : REPORT_BUNDLE_DEFINITION_ENTRY;
  const unsaved = (path: string) => files[path] !== bundle.draftFiles[path];
  const openCode = (path: string) => openFile(path, 'code');
  const setView = (next: FileView) => { if (current) setViews((all) => ({ ...all, [current]: next })); };
  const savedState = saving ? I18n.get('saving') : dirty ? I18n.get('reportBundleUnsaved') : I18n.get('designerSavedAgo', { ago: relativeTime(bundle.draftUpdatedAt ?? new Date()) });

  const activities: Activity<SideView>[] = [
    { id: 'files', label: I18n.get('reportBundleFiles'), icon: Files },
    { id: 'assistant', label: I18n.get('reportAssistant'), icon: Sparkles, mark: assistant.running ? 'busy' : null },
    { id: 'tests', label: I18n.get('designerTests'), icon: FlaskConical, mark: !compiles || failing > 0 ? 'bad' : null },
    { id: 'history', label: I18n.get('designerHistory'), icon: History },
  ];

  const fixtureChoices: PreviewChoice[] = fixturePaths.map((path) => ({
    key: path,
    label: baseName(path),
    file: true,
    result: results.find((r) => r.file === path),
    checked: source.kind === 'fixture' && source.path === path,
    onSelect: () => setPreviewSource({ kind: 'fixture', path }),
  }));
  const otherChoices: PreviewChoice[] = readsInputs
    ? [
      { key: 'try', label: I18n.get('designerPreviewTry'), note: Object.keys(tried).length > 0 ? I18n.get('designerNotSaved') : undefined, checked: source.kind === 'try', onSelect: () => setPreviewSource({ kind: 'try' }) },
      { key: 'empty', label: I18n.get('designerPreviewEmpty'), checked: source.kind === 'empty', onSelect: () => setPreviewSource({ kind: 'empty' }) },
    ]
    : [
      { key: 'sample', label: I18n.get('designerPreviewSample'), checked: source.kind === 'run' && !previewRun, onSelect: () => { setContext(sampleRunReportContext()); setPreviewRun(null); remember(previewRunKey(id), null); setPreviewSource({ kind: 'run' }); } },
      ...(previewRun ? [{ key: 'loaded', label: previewRun.title, checked: source.kind === 'run', onSelect: () => setPreviewSource({ kind: 'run' }) }] : []),
      { key: 'real', label: I18n.get('designerPreviewRealRun'), checked: false, onSelect: () => setShowRun(true) },
    ];

  const pickRulesFile = current && isRulesPath(current) ? current : paths.find(isRulesPath) ?? DEFAULT_RULES_FILE;

  /** The open file, in the view picked; its code is Monaco's, below. */
  const visual = (path: string): ReactNode => {
    switch (fileKind(path)) {
      case 'schema':
        return (
          <InputsTab
            text={pinnedSheet ? writeSheetFile(pinnedSheet) : files[INPUT_SHEET_FILE]}
            diagnostics={diagnostics}
            mode={view === 'try' ? 'try' : 'design'}
            readOnly={!!pinnedSheet}
            onChange={(text) => handleChange(INPUT_SHEET_FILE, text)}
            onOpenCode={pinnedSheet ? undefined : () => setView('code')}
            tried={tried}
            onTry={(entered) => { setTried(entered); setPreviewSource({ kind: 'try' }); }}
            tryFooter={(
              <TryItBar
                verdict={triedVerdict}
                fixtures={fixturePaths}
                existing={Object.keys(files)}
                onStartFrom={(fixture) => { setTried(parseFixture(files[fixture])?.inputs ?? {}); setPreviewSource({ kind: 'try' }); }}
                onClear={() => setTried({})}
                onSave={saveTriedAsFixture}
              />
            )}
          />
        );
      case 'fixture':
        return (
          <FixtureView
            key={path}
            path={path}
            text={files[path] ?? ''}
            sheet={readsInputs ? pinnedSheet ?? readSheetFile(files[INPUT_SHEET_FILE]).sheet : null}
            result={results.find((r) => r.file === path) ?? null}
            compiled={compiled}
            files={files}
            onChange={(text) => handleChange(path, text)}
            onOpenCode={() => setView('code')}
            onPick={compiled ? startPick : undefined}
            onOpenFile={(file) => openFile(file)}
          />
        );
      case 'test':
        return <RulesView key={path} path={path} text={files[path] ?? ''} results={results} onChange={(text) => handleChange(path, text)} onOpenCode={() => setView('code')} onPick={compiled ? startPick : undefined} onOpenFile={(file) => openFile(file)} />;
      case 'schemaRef':
        return <SchemaRefView text={files[path] ?? ''} schema={schema} onChange={(text) => handleChange(path, text)} onOpenCode={() => setView('code')} onOpenSchema={schema ? leaveTo(`/report-studio/schemas/${schema.id}`) : undefined} />;
      default:
        if (path === REPORT_BUNDLE_DEFINITION_ENTRY) return <DefinitionView compiled={compiled} onOpenCode={() => setView('code')} />;
        return (
          <DocumentOutlineView
            outline={outline}
            source={files[REPORT_BUNDLE_DOCUMENT_ENTRY] ?? ''}
            previewing={previewLabel}
            onOpenCode={() => setView('code')}
            onReveal={(line) => { openCode(REPORT_BUNDLE_DOCUMENT_ENTRY); setReveal({ path: REPORT_BUNDLE_DOCUMENT_ENTRY, line }); }}
          />
        );
    }
  };

  /** What the open file's header says beside its name, and its own links. */
  const header = (path: string): { note?: ReactNode; extra?: ReactNode } => {
    const kind = fileKind(path);
    if (kind === 'generated') return { note: I18n.get('designerGeneratedNote') };
    if (kind !== 'schema' || !schema) return {};
    const others = schema.usedBy.filter((u) => u.reportBundleId !== id);
    return {
      note: pinnedSheet ? I18n.get('designerSchemaPinnedNote', { title: schema.title, number: String(schema.pinnedVersion) }) : schemaLine,
      extra: (
        <>
          <span className="hidden text-xs text-muted-foreground lg:inline">{others.length ? I18n.get('designerAlsoReadBy', { reports: others.map((u) => u.title).join(', ') }) : I18n.get('designerOnlyThisReport')}</span>
          <Link to={`/report-studio/schemas/${schema.id}`} onClick={leaveTo(`/report-studio/schemas/${schema.id}`)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="size-3" /> {I18n.get('designerOpenSchemaAlone')}</Link>
        </>
      ),
    };
  };
  const { note, extra } = current ? header(current) : {};

  const onKeyDown = (e: KeyboardEvent) => {
    // Monaco saves on its own; everywhere else in the designer saves the same way
    if ((e.metaKey || e.ctrlKey) && e.key === 's' && !e.defaultPrevented) {
      e.preventDefault();
      if (dirty && !saving) void handleSave();
    }
  };

  return (
    // the designer fills the window and never widens it: a long title truncates instead
    <div data-slot="report-studio" className="flex h-svh min-w-0 flex-col bg-background text-sm text-foreground" style={{ contain: 'inline-size' }} onKeyDown={onKeyDown}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-2">
        <IconLink label={I18n.get('reportBundleBackToList')} to="/report-studio" onClick={leaveTo('/report-studio')}><ArrowLeft /></IconLink>
        <KindChip kind="report" />
        <h1 className="min-w-16 truncate text-sm font-semibold" title={bundle.description || bundle.title}>{bundle.title}</h1>
        <span className="hidden shrink-0 text-xs whitespace-nowrap text-muted-foreground sm:inline">
          {latest ? `v${latest.number}${latest.createdAt ? ` · ${new Date(latest.createdAt).toLocaleDateString()}` : ''}` : I18n.get('reportBundleNeverPublished')}
        </span>
        {unpublished && <UnpublishedChip />}
        <span className="ml-auto hidden text-xs whitespace-nowrap text-muted-foreground md:inline" title={bundle.draftUpdatedAt ? new Date(bundle.draftUpdatedAt).toLocaleString() : undefined}>{savedState}</span>
        <Button variant="outline" size="sm" onClick={() => void handleSave()} disabled={!dirty || saving} aria-label={I18n.get('reportBundleSaveDraft')}>
          <Save /> <span className="hidden lg:inline">{I18n.get('reportBundleSaveDraft')}</span>
        </Button>
        <Button variant="outline" size="sm" aria-pressed={previewOpen} onClick={togglePreview} className={cn(previewOpen && 'bg-muted')}>
          <Eye /> {I18n.get('reportBundlePreview')}
        </Button>
        <IconButton label={I18n.get('designerReportSettings')} onClick={() => setShowSettings(true)}><Settings /></IconButton>
        <Button size="sm" onClick={() => setShowPublish(true)} disabled={saving || (!dirty && !compiles)} aria-label={I18n.get('reportBundlePublishVersion')}>
          <Send /> {I18n.get('designerPublishNext', { number: String((latest?.number ?? 0) + 1) })}
        </Button>
      </header>

      {sheetConflict && schema && (
        <div role="alert" className="flex shrink-0 flex-wrap items-center gap-2 border-b border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-950">
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            {schema.draftUpdatedBy && schema.draftUpdatedAt
              ? I18n.get('designerConflictBy', { who: shortName(schema.draftUpdatedBy), title: schema.title, ago: relativeTime(schema.draftUpdatedAt) })
              : I18n.get('rv2SheetConflict', { title: schema.title })}
          </span>
          <Button variant="outline" size="xs" onClick={() => setComparing(true)} disabled={saving}>{I18n.get('designerCompare')}</Button>
          <Button variant="outline" size="xs" onClick={() => resolveSheetConflict('theirs')} disabled={saving}>{I18n.get('rv2SheetConflictTheirs')}</Button>
          <Button variant="outline" size="xs" onClick={() => resolveSheetConflict('mine')} disabled={saving}>{I18n.get('rv2SheetConflictMine')}</Button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <ActivityBar items={activities} active={sideOpen ? sideView : null} onPick={pickSide} />
        <ResizablePanelGroup id="report-designer" className="min-h-0 flex-1" defaultLayout={startLayout} onLayoutChanged={designerLayout.onLayoutChanged}>
          <ResizablePanel id="side" panelRef={sidePanel} groupResizeBehavior="preserve-pixel-size" collapsible collapsedSize={0} minSize={220} onResize={(size) => setSideOpen(size.asPercentage > 0)}>
            <div className="h-full" inert={!sideOpen}>
              {sideView === 'files' && (
                <Explorer
                  paths={paths}
                  active={current ?? ''}
                  working={files}
                  saved={bundle.draftFiles}
                  published={published}
                  results={results}
                  schemaMeta={schema ? `${schema.title} · ${which}` : undefined}
                  undeletable={UNDELETABLE}
                  onOpen={(path) => openFile(path)}
                  onNew={handleNewFile}
                  onDelete={handleDeleteFile}
                />
              )}
              {/* the assistant stays mounted under the other views: it keeps working while the author looks elsewhere */}
              <div className="h-full" hidden={sideView !== 'assistant'} inert={sideView !== 'assistant'}>
                <BundleAssistantPanel assistant={assistant} status={assistantStatus} files={files} previewLabel={assistantPreviewLabel} onClose={() => pickSide('assistant')} />
              </div>
              {sideView === 'tests' && <TestsView results={results} compiles={compiles} saving={saving} onOpen={(path) => openFile(path)} onRun={() => void handleSave()} />}
              {sideView === 'history' && (
                <div className="flex h-full flex-col bg-muted/30">
                  <SidebarHeader title={I18n.get('designerHistory')} />
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <BundleVersionsPanel bundleId={id} versions={versions} onRestore={(restored) => { setFiles((working) => restoreKeepingSchema(restored, working)); setDirty(true); openFile(REPORT_BUNDLE_DEFINITION_ENTRY); }} />
                  </div>
                </div>
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle />

          <ResizablePanel id="main" minSize={360}>
            <div className="flex h-full min-w-0 flex-col" style={{ contain: 'inline-size' }}>
              {openTabs.length > 0 && <FileTabs paths={openTabs} active={current} unsaved={unsaved} onPick={(path) => openFile(path)} onClose={closeFile} />}
              {current && <FileHeader path={current} note={note} views={fileViews} view={view} onView={setView}>{extra}</FileHeader>}
              <div className="relative min-h-0 flex-1">
                {!current && (
                  <div className="flex h-full flex-col items-center justify-center gap-2 p-[1.5rem] text-center text-muted-foreground">
                    <p>{I18n.get('designerNothingOpen')}</p>
                    <Button variant="outline" size="sm" onClick={() => showSide('files')}><Files /> {I18n.get('reportBundleFiles')}</Button>
                  </div>
                )}
                {current && !showCode && <div className="h-full">{visual(current)}</div>}
                <div className="h-full" hidden={!showCode} aria-label={I18n.get('reportBundleEditor')}>
                  <BundleCodeEditor files={files} active={codePath} onChange={handleChange} diagnostics={diagnostics} sdkTypes={sdkTypes} onSave={() => void handleSave()} readOnly={fileKind(codePath) === 'generated'} reveal={reveal} />
                </div>
              </div>
              {checks && <ChecksPanel tab={checks} onTab={setChecks} diagnostics={diagnostics} results={results} onSelect={(path) => (fileKind(path) === 'fixture' ? openFile(path) : openCode(path))} onHide={() => setChecks(null)} />}
            </div>
          </ResizablePanel>
          <ResizableHandle />

          {/* the report's own width (a wide table, a chart) scrolls inside the preview; it never widens the page */}
          <ResizablePanel id="preview" panelRef={previewPanel} groupResizeBehavior="preserve-pixel-size" collapsible collapsedSize={0} minSize={300} onResize={(size) => setPreviewOpen(size.asPercentage > 0)}>
            <div className="h-full" inert={!previewOpen} style={{ contain: 'inline-size' }}>
              <PreviewSidebar
                label={previewLabel}
                groups={[{ key: 'fixtures', label: I18n.get('reportBundleFixtures'), choices: fixtureChoices }, { key: 'others', choices: otherChoices }]}
                onHide={togglePreview}
                banner={picking ? <PickBanner onDone={stopPick} /> : undefined}
                footer={picking && shownPick ? (
                  <PickCard
                    // what it is added to follows the preview: a card opened over one fixture never writes into another, nor into the rules unasked
                    key={source.kind === 'fixture' ? source.path : 'none'}
                    target={shownPick.target}
                    found={shownPick.found}
                    fixture={source.kind === 'fixture' ? source.path : null}
                    rulesFile={pickRulesFile}
                    locked={{
                      fixture: source.kind === 'fixture' && !fixtureTakesChecks(files[source.path]),
                      rules: pickRulesFile in files && readRulesFile(files[pickRulesFile]).problems.length > 0,
                    }}
                    onAdd={(check, scope) => addPicked(check, scope, source.kind === 'fixture' ? source.path : null, pickRulesFile)}
                    onCancel={() => setPicked(null)}
                  />
                ) : undefined}
              >
                <ReportOutlineProvider store={outline}>
                  <BundlePreview compiled={compiled} context={preview.context} values={preview.values} stale={dirty} onPick={picking ? (pick) => setPicked({ ...pick, in: sourceKey }) : undefined} />
                </ReportOutlineProvider>
              </PreviewSidebar>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <StatusBar>
        <StatusItem tone={compiles ? 'ok' : 'bad'} onClick={() => setChecks('problems')}>{I18n.get(compiles ? 'designerCompiles' : 'designerDoesNotCompile')}</StatusItem>
        {diagnostics.length > 0 && <StatusItem tone="bad" onClick={() => setChecks('problems')}>{I18n.get('designerProblemCount', { count: String(diagnostics.length) })}</StatusItem>}
        <StatusItem tone={failing ? 'bad' : results.length ? 'ok' : undefined} onClick={() => setChecks('tests')}>
          {results.length === 0 ? I18n.get('reportBundleNoFixtures') : failing ? I18n.get('designerTestsFailing', { failing: String(failing), total: String(results.length) }) : printedChecks.pending ? I18n.get('designerTestsChecking') : I18n.get(results.length === 1 ? 'designerTestsHoldOne' : 'designerTestsHold', { total: String(results.length) })}
        </StatusItem>
        {schemaLine && <StatusItem className="hidden md:flex">{schemaLine}</StatusItem>}
        {previewOpen && <StatusItem className="hidden lg:flex">{I18n.get('designerPreviewing', { source: previewLabel })}</StatusItem>}
        <StatusItem className="ml-auto">{savedState}</StatusItem>
      </StatusBar>

      {comparing && sheetConflict && schema && (
        <CompareSheetsDialog
          title={schema.title}
          theirs={bundle.draftFiles[INPUT_SHEET_FILE] ?? ''}
          mine={files[INPUT_SHEET_FILE] ?? ''}
          by={schema.draftUpdatedBy}
          at={schema.draftUpdatedAt}
          busy={saving}
          onTheirs={() => { setComparing(false); resolveSheetConflict('theirs'); }}
          onMine={() => { setComparing(false); resolveSheetConflict('mine'); }}
          onHide={() => setComparing(false)}
        />
      )}
      {showPublish && <PublishBundleModal bundleId={id} bundleTitle={bundle.title} schema={schema} sheetText={files[INPUT_SHEET_FILE]} failing={dirty || sheetMoved ? [] : results.filter((r) => !r.ok)} checking={!dirty && !sheetMoved && printedChecks.pending} onHide={() => setShowPublish(false)} onPublish={handlePublish} />}
      {showSettings && (
        <SettingsDialog
          kind="report"
          title={bundle.title}
          description={bundle.description}
          schemaLine={schemaLine}
          onOpenSchemaRef={SCHEMA_REF_FILE in files ? () => { setShowSettings(false); openFile(SCHEMA_REF_FILE); } : undefined}
          onHide={() => setShowSettings(false)}
          onSave={async (data) => {
            // refused, the dialog stays open with what was typed, as the schema's does
            const saved = await updateMeta(id, data);
            if (saved) addToast({ type: FusaToastType.SUCCESS, header: I18n.get('reportBundleDetailsSaved'), message: data.title });
            return saved;
          }}
          onDelete={() => { setShowSettings(false); setShowDelete(true); }}
        />
      )}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{I18n.get('reportBundleDeleteBundle')}</AlertDialogTitle>
            <AlertDialogDescription>{I18n.get('reportBundleDeleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{I18n.get('cancel')}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => { setShowDelete(false); void deleteBundle(id).then((deleted) => { if (deleted) void navigate('/report-studio'); }); }}>{I18n.get('delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {showRun && (
        <Dialog open onOpenChange={(open) => { if (!open) setShowRun(false); }}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{I18n.get('designerPreviewRunTitle')}</DialogTitle>
              <DialogDescription>{I18n.get('designerPreviewRunHelp')}</DialogDescription>
            </DialogHeader>
            <div className="max-h-[65vh] overflow-auto rounded-lg border-[1px]">
              <BundleContextPanel
                context={context}
                onChange={(next) => { setContext(next); setPreviewRun(null); remember(previewRunKey(id), null); setPreviewSource({ kind: 'run' }); }}
                onLoadRun={async (runId) => {
                  const problem = await loadRun(runId);
                  if (!problem) setPreviewSource({ kind: 'run' });
                  return problem;
                }}
                onReset={() => { setContext(sampleRunReportContext()); setPreviewRun(null); remember(previewRunKey(id), null); setPreviewSource({ kind: 'run' }); }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
      {adding && <AddFileModal kind={adding} existing={paths} onHide={() => setAdding(null)} onAdd={(path) => addFile(path, '', 'code')} />}
    </div>
  );
}

/**
 * Publishing a version. A bundle that follows its schema's draft publishes
 * against it: when the draft is not a published version yet, it is published
 * too, and compared first with the schema's latest version — what it would
 * break is listed, since anything reading the inputs by id stops finding them,
 * and a report on the new version cannot read records entered on the old. It
 * warns, not blocks. Fixtures that missed their verdict on the last save do
 * block: the server would refuse, so they are said here, with what each came
 * to and why.
 */
function PublishBundleModal({ bundleId, bundleTitle, schema, sheetText, failing, checking, onHide, onPublish }: {
  bundleId: string;
  bundleTitle: string;
  schema: BundleSchemaInfo | null;
  sheetText: string | undefined;
  /** fixtures missing their verdict as the draft was last saved; empty when there are unsaved changes, which publishing saves and compiles first */
  failing: FixtureResult[];
  /** the printed document's checks are still being made: publishing waits for them */
  checking: boolean;
  onHide: () => void;
  /** false keeps the dialog open: what was saved first misses a check, shown here once made */
  onPublish: (note: string, publishSchema: boolean) => Promise<boolean>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { publishesSchema, changes } = useMemo(() => {
    const now = readSheetFile(sheetText).sheet;
    const latest = schema?.latestVersion ?? null;
    const publishes = !!schema && schema.pinnedVersion === null && !!now && !(latest && sameSheet(latest.sheet, now));
    return { publishesSchema: publishes, changes: publishes && latest && now ? sheetIdChanges(latest.sheet, now) : [] };
  }, [schema, sheetText]);
  const sharedWith = (schema?.usedBy ?? []).filter((u) => u.reportBundleId !== bundleId).length;
  const nextSchemaVersion = (schema?.latestVersion?.number ?? 0) + 1;
  // the button and Enter in the note alike: nothing is published while a check misses or is still being made
  const canPublish = !busy && !checking && failing.length === 0;
  const submit = () => {
    if (!canPublish) return;
    setBusy(true);
    void onPublish(note, publishesSchema).then((close) => { setBusy(false); if (close) onHide(); }, () => { setBusy(false); onHide(); });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onHide(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{I18n.get('reportBundlePublishVersion')}</DialogTitle>
          <DialogDescription>{I18n.get('reportBundlePublishConfirm', { title: bundleTitle })}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="publish-note">{I18n.get('reportBundlePublishNote')}</Label>
          <Input id="publish-note" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !busy) submit(); }} />
        </div>
        {publishesSchema && schema && (
          <p className="text-sm text-muted-foreground">
            {sharedWith
              ? I18n.get('rv2PublishesSchemaShared', { title: schema.title, number: String(nextSchemaVersion), count: String(sharedWith) })
              : I18n.get('rv2PublishesSchema', { title: schema.title, number: String(nextSchemaVersion) })}
          </p>
        )}
        {schema?.latestVersion && changes.length > 0 && <IdChanges changes={changes} title={schema.title} since={schema.latestVersion.number} />}
        {failing.length > 0 && (
          <div role="alert" className="flex flex-col gap-1.5 rounded-lg border-[1px] border-destructive/40 bg-destructive/5 p-[0.75rem] text-sm text-destructive">
            <p className="font-medium">{I18n.get('reportBundleFixturesFailingTitle')}</p>
            <ul className="list-disc pl-[1.25rem] text-xs">{failing.map((f) => <li key={f.file}>{describeFixtureMiss(f)}</li>)}</ul>
            <p className="text-xs">{I18n.get('reportBundleFixturesFailingHelp')}</p>
          </div>
        )}
        {checking && <p className="text-sm text-muted-foreground">{I18n.get('reportBundlePublishChecking')}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onHide} disabled={busy}>{I18n.get('cancel')}</Button>
          <Button disabled={!canPublish} onClick={submit}>{I18n.get('publish')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IdChanges({ changes, title, since }: { changes: SheetIdChange[]; title: string; since: number }) {
  return (
    <div role="alert" className="flex flex-col gap-1.5 rounded-lg border-[1px] border-amber-300 bg-amber-50 p-[0.75rem] text-sm text-amber-950">
      <p className="font-medium">{I18n.get('rv2IdChangesTitle', { title, number: String(since) })}</p>
      <IdChangeList changes={changes} shown={8} />
      <p className="text-xs">{I18n.get('rv2IdChangesHelp')}</p>
    </div>
  );
}

/** whether a check can be added to a fixture as it is now: it reads, and every check in it does */
function fixtureTakesChecks(text: string | undefined): boolean {
  const parsed = parseFixture(text);
  return !!parsed && readFixtureChecks(parsed.expect).problems.length === 0;
}

/** what a new file of each kind starts named */
const NEW_FILE_NAMES: Record<NewFileKind, string> = { fixture: 'fixtures/new-fixture.json', rules: 'tests/rules.json', module: 'helpers.ts', component: 'Section.tsx', other: '' };

function AddFileModal({ kind, existing, onHide, onAdd }: { kind: NewFileKind; existing: string[]; onHide: () => void; onAdd: (path: string) => void }) {
  const [path, setPath] = useState(NEW_FILE_NAMES[kind]);
  // the sheet's types are written from inputs.json, never by hand
  const valid = isValidBundlePath(path) && !existing.includes(path) && path !== INPUT_SHEET_TYPES_FILE;
  const submit = () => { if (!valid) return; onAdd(path); onHide(); };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onHide(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{I18n.get('reportBundleAddFile')}</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="bundle-file-path">{I18n.get('reportBundleAddFilePrompt')}</Label>
          <Input id="bundle-file-path" className="font-mono" value={path} aria-invalid={path.length > 0 && !valid} onChange={(e) => setPath(e.target.value.trim())} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onHide}>{I18n.get('cancel')}</Button>
          <Button disabled={!valid} onClick={submit}>{I18n.get('reportBundleAddFile')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
